import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentFile } from 'src/app/_Models/fileupload/fileupload';
import { FileStoreService } from 'src/app/_Services/filestore/filestore.service';
import { QuotationSummaryService } from 'src/app/_Services/quotationNew/quotationsummary.service';
import { InvoiceSummaryService } from 'src/app/_Services/invoice/invoice-summary.service';

@Component({
    selector: 'app-supporting-invoice-documents-popup',
    templateUrl: './supporting-invoice-documents-popup.component.html',
    styleUrls: ['./supporting-invoice-documents-popup.component.scss']
})
export class SupportingInvoiceDocumentsPopupComponent implements OnInit {
    @Input() invoiceId: number;

    // Existing supporting documents (loaded from backend)
    existingDocuments: any[] = [];

    // New documents to upload
    public newDocuments: AttachmentFile[] = [];

    // Documents marked for deletion
    documentsToDelete: number[] = [];

    // Loading states
    isLoading: boolean = false;
    isSaving: boolean = false;
    isUploading: boolean = false;

    constructor(
        public activeModal: NgbActiveModal,
        private toastr: ToastrService,
        private translate: TranslateService,
        private invoiceService: InvoiceSummaryService,
        private fileService: FileStoreService
    ) { }

    ngOnInit(): void {
        console.log('SupportingInvoiceDocumentsPopup - invoiceId:', this.invoiceId);
        if (this.invoiceId) {
            this.loadSupportingDocuments();
        } else {
            console.error('SupportingInvoiceDocumentsPopup - No invoiceId provided!');
        }
    }

    // Load existing supporting documents from backend
    loadSupportingDocuments() {
        console.log('Loading supporting invoice documents for invoiceId:', this.invoiceId);
        this.isLoading = true;
        this.invoiceService.getSupportingDocuments(this.invoiceId).subscribe(
            (response) => {
                this.isLoading = false;
                if (response && response.length > 0) {
                    this.existingDocuments = response;
                    console.log('Loaded documents:', this.existingDocuments);
                } else {
                    this.existingDocuments = [];
                    console.log('No supporting documents found');
                }
            },
            (error) => {
                this.isLoading = false;
                this.toastr.error(this.translate.instant('INVOICE_SUMMARY.MSG_LOAD_ERROR'));
                console.error('Error loading supporting documents:', error);
            }
        );
    }

      newUniqueId() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }


    // Handle file selection for upload
    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const maxFileSize = 10485760; // 10MB

        if (input.files && input.files.length > 0) {
            for (let i = 0; i < input.files.length; i++) {
                const file = input.files[i];

                // Validate file size
                if (file.size > maxFileSize) {
                    this.toastr.error(
                        this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_FILE_TOO_LARGE', { fileName: file.name })
                    );
                    continue;
                }

                // Check for duplicate files in new documents
                const isDuplicateNew = this.newDocuments.some(doc => doc.fileName === file.name);
                if (isDuplicateNew) {
                    this.toastr.warning(
                        this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_FILE_ALREADY_EXISTS', { fileName: file.name })
                    );
                    continue;
                }

                // Check for duplicate files in existing documents
                const isDuplicateExisting = this.existingDocuments.some(doc => doc.fileName === file.name);
                if (isDuplicateExisting) {
                    this.toastr.warning(
                        this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_FILE_ALREADY_EXISTS', { fileName: file.name })
                    );
                    continue;
                }

                // Create attachment file object
                const attachmentFile = new AttachmentFile();
                attachmentFile.file = file;
                attachmentFile.fileName = file.name;
                attachmentFile.fileSize = file.size;
                attachmentFile.fileUrl = URL.createObjectURL(file);
                attachmentFile.uniqueld = this.newUniqueId();

                this.newDocuments.push(attachmentFile);
            }

            // Reset input
            input.value = '';
        }
    }

    // Generate unique ID for file
    generateUniqueId(fileName: string): string {
        const dotIndex = fileName.lastIndexOf('.');
        const nameWithoutExt = dotIndex !== -1 ? fileName.substring(0, dotIndex) : fileName;
        const extension = dotIndex !== -1 ? fileName.substring(dotIndex + 1) : '';
        const timestamp = new Date().getTime();
        const randomStr = Math.random().toString(36).substring(2, 8);

        const cleanName = nameWithoutExt
            .trim()
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_');

        return `${cleanName}_${timestamp}_${randomStr}${extension ? '.' + extension : ''}`;
    }

    // Remove new document before saving
    removeNewDocument(index: number) {
        if (this.newDocuments[index].fileUrl) {
            URL.revokeObjectURL(this.newDocuments[index].fileUrl);
        }
        this.newDocuments.splice(index, 1);
    }

    markForDeletion(documentId: number) {
        this.documentsToDelete.push(documentId);
        this.existingDocuments = this.existingDocuments.filter(doc => doc.id !== documentId);
    }

    // Download existing document
    downloadDocument(doc: any) {
        if (doc.fileUrl) {
            window.open(doc.fileUrl, '_blank');
        } else {
            this.toastr.error(this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_FILE_NOT_FOUND'));
        }
    }


    async saveDocuments() {

        if (this.isSaving) return;

        this.isSaving = true;
        this.isUploading = true;

        try {
            // -------------------------------
            // STEP 1: Upload new supporting documents
            // -------------------------------

            let uploadedSupportingDocs: Array<{ fileName: string, fileUrl: string, uniqueId: string }> = [];

            if (this.newDocuments.length > 0) {

                const uploadResponse = await this.fileService.uploadFileData(
                    19,                               // same container as quotation popup
                    this.newDocuments                 // list of new files
                );

                if (!uploadResponse || !uploadResponse.fileUploadDataList) {
                    throw new Error("Failed to upload supporting files");
                }

               uploadedSupportingDocs = uploadResponse.fileUploadDataList.map((file, index) => ({
     fileName: this.newDocuments[index].fileName,
     fileUrl: file.fileCloudUri,
     uniqueId: file.fileUniqueId
 }));
            }

            this.isUploading = false;

            // -------------------------------
            // STEP 2: If no uploads & no deletions → do nothing
            // -------------------------------
            if (uploadedSupportingDocs.length === 0 && this.documentsToDelete.length === 0) {
                this.toastr.info(this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_NO_CHANGES'));
                this.isSaving = false;
                return;
            }

            // -------------------------------
            // STEP 3: Prepare final save payload
            // -------------------------------
            const saveData = {
                invoiceId: this.invoiceId,
                newDocuments: uploadedSupportingDocs,
                documentsToDelete: this.documentsToDelete
            };

            // -------------------------------
            // STEP 4: Submit to backend
            // -------------------------------
            this.invoiceService.saveSupportingDocuments(saveData).subscribe(
                (response) => {

                    this.isSaving = false;

                    if (response && response.result === 1) {

                        this.toastr.success(this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_SAVE_SUCCESS'));

                        // Clean local new files
                        this.newDocuments.forEach(doc => {
                            if (doc.fileUrl) URL.revokeObjectURL(doc.fileUrl);
                        });

                        // reset local copy
                        this.newDocuments = [];
                        this.documentsToDelete = [];
                        this.activeModal.close({ success: true });

                        // reload fresh from server
                        this.loadSupportingDocuments();
                    }
                    else {
                        this.toastr.error(response.message || this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_SAVE_ERROR'));
                    }
                },
                (error) => {
                    this.isSaving = false;
                    this.toastr.error(this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_SAVE_ERROR'));
                    console.error("Save error:", error);
                }
            );

        } catch (error) {
            this.isSaving = false;
            this.isUploading = false;
            this.toastr.error(this.translate.instant('QUOTATIONNEW_SUMMARY.MSG_UPLOAD_ERROR'));
            console.error("Upload error:", error);
        }
    }

    // Cancel and close modal
    cancel() {
        // Clean up object URLs for new documents
        this.newDocuments.forEach(doc => {
            if (doc.fileUrl) {
                URL.revokeObjectURL(doc.fileUrl);
            }
        });

        this.activeModal.dismiss();
    }

    // Get file size in readable format
    getFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    
     getImageIcon(file) {

    if (file.fileName) {
      var splitFiles = file.fileName.split('.');
      var fileExtension = splitFiles[splitFiles.length - 1].toLocaleLowerCase();

      if (fileExtension == "pdf")
        return "assets/images/new-set/pdf-icon.svg";
      else if (fileExtension == "doc" || fileExtension == "docx")
        return "assets/images/new-set/word-icon.svg";
      else if (fileExtension == "png" || fileExtension == "jpg" || fileExtension == "jpeg")
        return "assets/images/new-set/image-icon.svg";
      else if (fileExtension == "xlsx" ||fileExtension == "xls" )
        return "assets/images/new-set/excel-icon.svg";

    }
}
}
